// ServiceFinder Quiz Data and Recommendation Logic

export interface QuizOption {
  id: string;
  label: string;
  description?: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
}

export interface ServiceRecommendation {
  serviceId: string;
  title: string;
  description: string;
  priority: "primary" | "secondary";
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: "goal",
    question: "What's your primary goal right now?",
    options: [
      {
        id: "leads",
        label: "Get more leads & customers",
        description: "Increase phone calls, form submissions, and sales",
      },
      {
        id: "website",
        label: "Build or improve my website",
        description: "Create a new site or upgrade an existing one",
      },
      {
        id: "system",
        label: "Create a custom business system",
        description: "CRM, booking platform, or custom software",
      },
      {
        id: "unsure",
        label: "I'm not sure yet",
        description: "I need guidance on what I should focus on",
      },
    ],
  },
  {
    id: "timeline",
    question: "What's your timeline?",
    options: [
      {
        id: "asap",
        label: "Need results ASAP (1-3 months)",
        description: "Ready to invest and see quick returns",
      },
      {
        id: "longterm",
        label: "Building for long-term (6+ months)",
        description: "Focused on sustainable growth",
      },
      {
        id: "exploring",
        label: "Just exploring options",
        description: "Gathering information for now",
      },
    ],
  },
  {
    id: "budget",
    question: "What's your monthly budget range?",
    options: [
      {
        id: "under1k",
        label: "Under $1,000/month",
      },
      {
        id: "1k-3k",
        label: "$1,000 - $3,000/month",
      },
      {
        id: "3k-10k",
        label: "$3,000 - $10,000/month",
      },
      {
        id: "10k+",
        label: "$10,000+/month",
      },
      {
        id: "unsure",
        label: "Not sure yet",
      },
    ],
  },
];

// Recommendation matrix based on user answers
export function getRecommendations(answers: Record<string, string>): ServiceRecommendation[] {
  const { goal, timeline, budget } = answers;
  const recommendations: ServiceRecommendation[] = [];

  // Not sure / exploring - always recommend consultation
  if (goal === "unsure" || timeline === "exploring") {
    return [
      {
        serviceId: "consultation",
        title: "Free Strategy Consultation",
        description:
          "Let's discuss your business goals and we'll recommend the right approach. No pressure, just honest advice.",
        priority: "primary",
      },
    ];
  }

  // LEADS goal
  if (goal === "leads") {
    if (timeline === "asap") {
      // Quick results - PPC/LSA
      recommendations.push({
        serviceId: "local-service-ads",
        title: "Local Service Ads",
        description:
          "Google's pay-per-lead program. You only pay for actual leads. Perfect for service businesses needing fast results.",
        priority: "primary",
      });
      recommendations.push({
        serviceId: "ppc",
        title: "PPC Advertising",
        description:
          "Targeted Google & Facebook ads that drive immediate traffic and leads to your business.",
        priority: "secondary",
      });
    } else if (timeline === "longterm") {
      // Long-term - SEO focused
      if (budget === "under1k" || budget === "1k-3k") {
        recommendations.push({
          serviceId: "seo",
          title: "Search Engine Optimization",
          description:
            "Build organic traffic that compounds over time. The foundation of sustainable lead generation.",
          priority: "primary",
        });
        recommendations.push({
          serviceId: "gmb-optimization",
          title: "Google Business Profile",
          description:
            "Optimize your local presence to appear in Google Maps and local search results.",
          priority: "secondary",
        });
      } else {
        // Higher budget - SEO + PPC combo
        recommendations.push({
          serviceId: "seo",
          title: "Search Engine Optimization",
          description:
            "Build organic traffic that compounds over time. The foundation of sustainable lead generation.",
          priority: "primary",
        });
        recommendations.push({
          serviceId: "ppc",
          title: "PPC Advertising",
          description:
            "Supplement SEO with paid ads for immediate results while organic rankings build.",
          priority: "primary",
        });
        recommendations.push({
          serviceId: "gmb-optimization",
          title: "Google Business Profile",
          description:
            "Optimize your local presence to appear in Google Maps and local search results.",
          priority: "secondary",
        });
      }
    }
  }

  // WEBSITE goal
  if (goal === "website") {
    recommendations.push({
      serviceId: "web-design",
      title: "Web Design & Development",
      description:
        "Custom websites built for conversions. Fast, mobile-friendly, and designed to turn visitors into customers.",
      priority: "primary",
    });

    if (timeline === "longterm") {
      recommendations.push({
        serviceId: "seo",
        title: "Search Engine Optimization",
        description:
          "Make your new website visible. SEO ensures people find you when searching for your services.",
        priority: "secondary",
      });
    }
  }

  // SYSTEM goal
  if (goal === "system") {
    recommendations.push({
      serviceId: "custom-business-solutions",
      title: "Custom Business Platforms",
      description:
        "CRM systems, booking platforms, inventory management—custom-built solutions for your unique needs.",
      priority: "primary",
    });
    recommendations.push({
      serviceId: "app-development",
      title: "App Development",
      description:
        "Mobile and web applications designed for your business operations and customer engagement.",
      priority: "secondary",
    });
  }

  // Fallback
  if (recommendations.length === 0) {
    return [
      {
        serviceId: "consultation",
        title: "Free Strategy Consultation",
        description:
          "Based on your answers, we'd love to discuss your specific needs. Book a free call to get personalized recommendations.",
        priority: "primary",
      },
    ];
  }

  return recommendations;
}
